import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import FindDealerStep from '../../4_Templates/FindDealerStep/FindDealerStep'
import ContactDetailsStep from '../../4_Templates/ContactDetailsStep/ContactDetailsStep'
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import ConfirmationStep from '../../4_Templates/ConfirmationStep/ConfirmationStep'
import { convertLangCodeToSingleLetter, displayOutsideComponents, hideOutsideComponents } from '../../../utils/helpers'
import pidController from '../../../controllers/pidController'
import { getEndpointUrl } from '../../../utils/endPoints'

function GetQuote({ formExtraParams }) {
  const { source, language, lastStepBottomContents, initStepTopContents } = formExtraParams
  const maxStepNum = 3;
  const initStep = 0;

  const [currentStep, setCurrentStep] = useState(initStep)
  const [currentProgress, setCurrentProgress] = useState(0)
  const [data, setData] = useState({})

  // Display top contents on the init step.
  useEffect(() => {
    if (initStep === 0) {
      displayOutsideComponents(initStepTopContents)
    }
  }, [])

  const handleNextStep = (newProps) => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === initStep + 1) {
      // Hide GaQ Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display GaQ related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    window.scrollTo(0, 0)
  }

  // Refresh data and go back to init step.
  const refreshSteps = () => {
    setCurrentStep(initStep)
    setCurrentProgress(initStep)
    setData({})
    displayOutsideComponents(initStepTopContents)
  }

  return (
    <div className="mz-get-quote mz-page-top" data-current-step={currentStep} >
      <FormStepsNav
        isActive={(currentStep > 0 && currentStep < maxStepNum)}
        currentStep={currentStep}
        currentProgress={currentProgress}
        setCurrentStep={setCurrentStep}
        refreshSteps={refreshSteps}
        refreshText={languageController.getCopy('global', 'vehicles')}
        navList={[
          {
            step: 1,
            label: languageController.getCopy('global', 'selectDealerNav'),
          },
          {
            step: 2,
            label: languageController.getCopy('global', 'contactDetailsNav'),
          },
        ]}
      />
      <div className="mz-get-quote__steps">
        {/* Step 0: Select a vehicle */}
        <StepWrapper
          className={`mz-get-quote__select-vehicle`}
          stepNum={0}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <SelectVehicleStep
            title={languageController.getCopy('global', 'selectVehicle')}
            handleNextStep={handleNextStep}
          />
        </StepWrapper>
        {/* Step 1: Select a dealer */}
        <StepWrapper
          className={`mz-get-quote__select-dealer`}
          type="select-dealer"
          stepNum={1}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <FindDealerStep
            handleNextStep={handleNextStep}
            vehicleData={data.vehicleData}
            title={languageController.getCopy('getQuote', 'step1Title')}
            subTitle={languageController.getCopy('global', 'findDealerSub')}
          />
        </StepWrapper>
        {/* Step 2: Enter Contact Details */}
        <StepWrapper
          className={`mz-get-quote__contact-details`}
          stepNum={2}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ContactDetailsStep
            handleNextStep={handleNextStep}
            title={languageController.getCopy('global', 'fillContactForm')}
            submitButtonText={languageController.getCopy('getQuote', 'step2Submit')}
            dealerData={data.dealerData}
            dateAndTimeData={data.dateAndTime}
            vehicleData={data.vehicleData}
            formSource={source}
            formPid={pidController.getPid('requestQuote')}
            formEndPointUrl={getEndpointUrl('request-quote')}
            formLanguage={convertLangCodeToSingleLetter(language)}
          />
        </StepWrapper>
        {/* Step 3: Show Confirmation */}
        <StepWrapper
          className={`mz-get-quote__confirmation`}
          stepNum={3}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ConfirmationStep
            vehicleData={data.vehicleData}
            dealerData={data.dealerData}
            contactDetails={data.contactDetails}
          />
        </StepWrapper>
      </div>
    </div>
  )
}

GetQuote.propTypes = {
  formExtraParams: PropTypes.object.isRequired
}

GetQuote.defaultProps = {
  formExtraParams: {}
}

export default GetQuote